/**
 * The fade to black that every beat opens and closes on.
 *
 * This lived twice, hand-rolled, in `bornDirector.ts` and `latchDirector.ts`,
 * and `latchDirector.ts` carried a comment saying so on purpose: extracting it
 * would have touched beat 1, which was finished, green and the only thing in the
 * project a human had actually played, and **two copies of twenty lines is a
 * coincidence rather than a pattern.** The rule written down at the time was
 * that it gets extracted when a third director needs it.
 *
 * `ottoDirector.ts` is the third. So here it is, and the comments below came
 * with it rather than being rewritten — the reasoning about why this is a DOM
 * element, why it starts opaque and why it sits at z-index 5 is the valuable
 * part, and all of it was paid for by bugs.
 */
export class FadeOverlay {
  readonly #overlay: HTMLDivElement;
  /**
   * Fade to black, 0..1. **Starts fully black, and that is a fix, not a default.**
   *
   * R-023, second note: *"It first shows Rosalie right away, then the camera is
   * panning from another spot towards her."* Both halves of that were real and
   * they were two different defects stacked on each other.
   *
   * This is the first. The overlay used to start transparent and only go black
   * when the `wake` cue arrived, so every frame rendered between the beat being
   * constructed and the sequencer's first tick was **the fully lit hall through
   * the gameplay camera** — the shot the opening is built to withhold. It is a
   * handful of frames and it is the first thing anybody sees.
   *
   * The second is in `BornDirector.framing()`.
   *
   * ── And one beat does NOT open on black, so it says so ──────────────────
   *
   * Beat 10 builds an overlay at room construction for a consequence that may
   * never fire — being driven back by the night dog — and a beat that opens on
   * a moonlit field it wants the player to read has nothing to withhold. Given
   * the default it opened at 0.33 and was still veiling the field at 0.135
   * eighteen seconds later, because `update` is fed the RENDER dt and a fast
   * simulation renders few frames: every evidence frame the beat produced came
   * out under a wash, which is ruinous in the one beat whose grade has to be
   * judged from its frames. `initialValue` is how a caller says *I am not an
   * opening*; the default is unchanged and is still the fix described above.
   */
  #value: number;
  #target: number;

  constructor(initialValue = 1) {
    // The fade is a DOM element rather than a fullscreen quad in the post stack.
    //
    // It has to cover the frame *including* the paper grain and the ink outlines,
    // and a quad inside the stack would be graded and grained along with
    // everything else — a fade that acquires a paper texture as it goes to black
    // is a very expensive-looking bug. It also has to work when the post stack is
    // disabled with `?post=0`.
    this.#overlay = document.createElement('div');
    this.#overlay.id = 'beat-fade';
    this.#overlay.setAttribute('aria-hidden', 'true');
    Object.assign(this.#overlay.style, {
      position: 'fixed',
      inset: '0',
      background: '#000',
      // Black from the moment the element exists, not from the first `update`.
      // See `#value`'s initialiser: the game must not be able to show a frame of
      // itself before the beat has said anything.
      opacity: '1',
      pointerEvents: 'none',
      // Above the canvas, below the pause overlay: pausing during a fade must
      // still show you the way out (that was the whole point of `pause.ts`).
      zIndex: '5',
    } satisfies Partial<CSSStyleDeclaration>);
    document.body.appendChild(this.#overlay);
    // Written through the same path `update` uses, so the element's opacity and
    // this value cannot disagree on the very first frame.
    this.#value = initialValue;
    this.#target = initialValue;
    this.#overlay.style.opacity = this.#value.toFixed(4);
  }

  get value(): number {
    return this.#value;
  }

  setTarget(target: number): void {
    this.#target = target;
  }

  /** A cut, not a fade: beat 14's black lands ON the bell's stroke, not after it. */
  cutTo(value: number): void {
    this.#value = value;
    this.#target = value;
    this.#overlay.style.opacity = value.toFixed(4);
  }

  snapToBlackAndFadeIn(): void {
    this.#value = 1;
    this.#target = 0;
  }

  update(frameDt: number): void {
    this.#value += (this.#target - this.#value) * (1 - Math.exp(-1.6 * frameDt));
    this.#overlay.style.opacity = this.#value.toFixed(4);
  }

  dispose(): void {
    this.#overlay.remove();
  }
}
